import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeSlash, Image as ImageIcon, VideoCamera } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import useAdminLoad from "@/hooks/useAdminLoad";
import { Empty, LoadState, PageHeader, Panel, Pill } from "@/components/admin/AdminUI";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import { api, selfieUrl } from "@/lib/api";
import { formatDate, formatDuration, humanize } from "@/lib/format";

const ACT = {
  overturn: { label: "Overturn", title: "Overturn and restore this photo?", desc: "It's added to their profile, and they get the “Photo restored notice” from Content.", destructive: false },
  confirm: { label: "Confirm rejection", title: "Keep this photo out?", desc: "The rejection stands. The file is purged when the retention period ends.", destructive: true },
};
const VIDEO_ACT = {
  overturn: { ...ACT.overturn, title: "Overturn and put this video intro live?", desc: "Their “Break my glass” intro goes live on their profile." },
  confirm: { ...ACT.confirm, title: "Keep this video intro out?" },
};
const isVideo = (q) => q?.media_type === "glass_video";
const actOf = (q, action) => (isVideo(q) ? VIDEO_ACT : ACT)[action];

// Private image (admin auth via ?auth=), blurred until the moderator chooses to look.
function BlurredImage({ item, compact = false }) {
  const [shown, setShown] = useState(false);
  return (
    <button type="button" onClick={() => setShown((s) => !s)} aria-pressed={shown} aria-label={shown ? "Blur photo" : "Show photo"}
      className={`group relative block aspect-[3/4] w-full overflow-hidden bg-[var(--cw-surface-tertiary)] ${compact ? "rounded-lg" : "rounded-2xl"}`} data-testid={`rejected-image-toggle-${item.id}`}>
      <img src={selfieUrl(item.image_url)} alt="Rejected upload" className={`h-full w-full object-cover transition-all duration-300 ${shown ? "" : "scale-110 blur-2xl"}`} data-testid={`rejected-image-${item.id}`} />
      {compact ? !shown && <Eye size={14} className="absolute inset-0 m-auto text-white drop-shadow" aria-hidden /> : <span className={`absolute inset-x-3 bottom-3 inline-flex items-center justify-center gap-2 rounded-full bg-white/90 px-3 py-2 text-xs font-medium ${shown ? "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100" : ""}`}>
        {shown ? <><EyeSlash size={14} />Blur again</> : <><Eye size={14} />Click to view</>}
      </span>}
    </button>
  );
}

// Private mp4 (admin auth via ?auth=, byte ranges), hidden behind a blur until the moderator chooses to watch.
function BlurredVideo({ item }) {
  const [shown, setShown] = useState(false);
  const [failed, setFailed] = useState(false);
  if (shown) return (
    <div className="space-y-2">
      <video src={selfieUrl(item.video_url)} controls autoPlay playsInline preload="metadata" onError={() => setFailed(true)} className="aspect-[3/4] w-full rounded-2xl bg-black object-contain" data-testid={`rejected-video-${item.id}`} />
      {failed && <p className="cw-error text-xs" role="alert" data-testid={`rejected-video-error-${item.id}`}>This browser can’t play the video. <a href={selfieUrl(item.video_url)} download className="underline">Download the mp4</a></p>}
      <Button size="sm" variant="ghost" className="w-full" onClick={() => { setShown(false); setFailed(false); }} data-testid={`rejected-video-hide-${item.id}`}><EyeSlash size={14} />Hide video</Button>
    </div>
  );
  return (
    <button type="button" onClick={() => setShown(true)} aria-label="Watch video" className="group relative block aspect-[3/4] w-full overflow-hidden rounded-2xl bg-[var(--cw-surface-tertiary)]" data-testid={`rejected-video-toggle-${item.id}`}>
      {item.image_url && <img src={selfieUrl(item.image_url)} alt="" className="h-full w-full scale-110 object-cover blur-2xl" />}
      <span className="absolute inset-x-3 bottom-3 inline-flex items-center justify-center gap-2 rounded-full bg-white/90 px-3 py-2 text-xs font-medium"><Eye size={14} />Click to watch</span>
    </button>
  );
}

function VideoFacts({ q }) {
  return (
    <div className="flex items-center gap-3" data-testid={`rejected-video-facts-${q.id}`}>
      {q.image_url && <div className="w-14 shrink-0"><BlurredImage item={q} compact /></div>}
      <p className="cw-soft text-xs">
        <span className="tabular-nums" data-testid={`rejected-duration-${q.id}`}>{q.duration_s != null ? formatDuration(q.duration_s) : "—"}</span> long ·{" "}
        <span className="tabular-nums" data-testid={`rejected-frames-${q.id}`}>{q.frames_checked ?? "—"}</span> frames checked
        {q.image_url && <span className="block">Left: the worst-scoring frame</span>}
      </p>
    </div>
  );
}

function Card({ q, onAct }) {
  const video = isVideo(q);
  return (
    <li data-testid={`rejected-card-${q.id}`}>
      <Panel className="flex h-full flex-col gap-4 p-4">
        {video ? (q.video_url ? <BlurredVideo item={q} /> : <p className="cw-muted rounded-2xl bg-[var(--cw-surface-tertiary)] p-6 text-center text-sm" data-testid={`rejected-video-missing-${q.id}`}>Video file no longer available</p>) : <BlurredImage item={q} />}
        {video && <VideoFacts q={q} />}
        <div className="space-y-1 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Pill tone="neutral" data-testid={`rejected-type-${q.id}`}>{video ? <><VideoCamera size={12} />Video intro</> : <><ImageIcon size={12} />Photo</>}</Pill>
            <Pill tone="warning" data-testid={`rejected-reason-${q.id}`}>{humanize(q.reason)}</Pill>
            {q.score != null && <span className="cw-soft tabular-nums" data-testid={`rejected-score-${q.id}`}>score {Number(q.score).toFixed(2)}</span>}
          </div>
          <Link to={`/admin/users/${q.user_id}`} className="block truncate font-mono text-xs hover:underline" data-testid={`rejected-user-${q.id}`}>{q.user_id}</Link>
          <p className="cw-muted text-xs">Uploaded {formatDate(q.created_at)} · purged {formatDate(q.expires_at, false)}</p>
          {q.decided_by && <p className="cw-muted text-xs">{humanize(q.status)} by {q.decided_by}, {formatDate(q.decided_at)}</p>}
        </div>
        {q.status === "pending" && (
          <div className="mt-auto flex gap-2">
            <Button size="sm" className="flex-1" onClick={() => onAct(q, "overturn")} data-testid={`rejected-overturn-button-${q.id}`}>Overturn</Button>
            <Button size="sm" variant="outline" className="flex-1" onClick={() => onAct(q, "confirm")} data-testid={`rejected-confirm-button-${q.id}`}>Confirm</Button>
          </div>
        )}
      </Panel>
    </li>
  );
}

export default function RejectedPhotos() {
  const [status, setStatus] = useState("pending");
  const { data, error, loading, reload, setData } = useAdminLoad(`/admin/photos/rejected?status=${status}`);
  const [ask, setAsk] = useState(null);
  const [reason, setReason] = useState("");
  const days = data?.retention_days ?? 30;

  const decide = async () => {
    try {
      await api(`/admin/photos/rejected/${ask.q.id}/${ask.action}`, { body: { reason: reason.trim() } });
    } catch (e) {
      throw e.code === "too_many_photos" ? new Error("Their profile already has the most photos allowed. Ask them to remove one first.") : e;
    }
    setData((d) => ({ ...d, items: d.items.filter((x) => x.id !== ask.q.id), total: Math.max(0, (d.total || 1) - 1) }));
    toast.success(ask.action === "overturn" ? (isVideo(ask.q) ? "Video intro is live." : "Photo restored to their profile.") : "Rejection confirmed.");
  };

  return (
    <div data-testid="admin-rejected-photos-page">
      <PageHeader title="Rejected media" description="Photos and video intros the automatic check turned away. Take a second look, then overturn or confirm." />
      <p className="cw-tone-neutral mb-6 rounded-2xl bg-[var(--cw-surface-tertiary)] px-4 py-3 text-sm" data-testid="rejected-retention-note">
        Rejected uploads are kept privately for {days} day{days === 1 ? "" : "s"}, then deleted for good. Photos and videos stay blurred until you click them.
      </p>
      <Tabs value={status} onValueChange={setStatus} className="mb-6">
        <TabsList>{["pending", "overturned", "confirmed"].map((s) => <TabsTrigger key={s} value={s} className="capitalize" data-testid={`rejected-tab-${s}`}>{s}</TabsTrigger>)}</TabsList>
      </Tabs>
      <LoadState loading={loading && !data} error={error} onRetry={reload}>
        <p className="cw-muted mb-4 text-xs" data-testid="rejected-total">{data?.total ?? 0} {status}</p>
        {data?.items?.length ? (
          <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="rejected-list">{data.items.map((q) => <Card key={q.id} q={q} onAct={(x, action) => { setReason(""); setAsk({ q: x, action }); }} />)}</ul>
        ) : <Empty title={status === "pending" ? "Nothing to review" : `No ${status} items`} text="Rejected uploads appear here for review." testId="rejected-empty" />}
      </LoadState>
      <ConfirmDialog open={!!ask} onOpenChange={(o) => !o && setAsk(null)} onConfirm={decide} testId="rejected-decide-dialog"
        title={ask && actOf(ask.q, ask.action).title} description={ask && actOf(ask.q, ask.action).desc} confirmLabel={ask && actOf(ask.q, ask.action).label}
        destructive={ask ? actOf(ask.q, ask.action).destructive : false} confirmDisabled={reason.trim().length < 3}>
        <div><Label htmlFor="rejected-reason">Reason</Label>
          <Textarea id="rejected-reason" rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} className="mt-2" data-testid="rejected-reason-input" /></div>
      </ConfirmDialog>
    </div>
  );
}
